/**
 * A Beschreibung file as the admin portal shows and edits it: lines, sections and rows in the standard
 * columns, and a draft of a new version made from an existing one (backlog #56, Track #2610015).
 *
 * **IRES Cloud stays the judge.** It checks every draft before it is published
 * (`checkInfoConfigUploadForAdministration`); this file only splits a file for display and edits it
 * without disturbing what is not edited. The splitting follows the same rules as IRES Cloud's parser
 * (Cloud `services/infoConfig/infoConfig.ts`), so the two agree on keys, sections and option indexes:
 * - the separator is the last character of the first line (`;`, or `,` in a few old versions);
 * - a quoted cell may hold the separator and `""` is one quote; quote artefacts around a cell are
 *   stripped for display;
 * - `#Name` without a digit starts a section, with a digit it is a data row; `?…` lines are comments;
 * - a data row is `key;category;group;entry;trade;type;values…` (type E, M, T or A); in a selection
 *   row the options are the value cells without blanks and `/`, counted from 0, which is the index a
 *   project stores; the portal shows them counted from 1, as the file's own references name them.
 *
 * **An edit touches only its own cell.** Every other line and every other cell, the BOM and each
 * line's own ending (the files end lines with `\r\r\n`) stay byte for byte, so the diff IRES Cloud shows
 * is exactly the edit. An edited cell is edited in its CSV value, not its display: a leading `"` there is
 * IRES Project's "hide in description" marker (`"""(Prozent) betroffener Anteil"` holds
 * `"(Prozent) betroffener Anteil"`), and it survives the edit.
 */

/** The standard columns between the key and the values. */
export const LABEL_COLUMNS = ["category", "group", "entry", "trade"] as const;

const TYPE_MARKERS = new Set(["E", "M", "T", "A"]);

/** Where the type cell sits in the standard layout (key at 0). */
const TYPE_CELL = LABEL_COLUMNS.length + 1;

export interface BeschreibungRow {
  /** 0-based position in the file's lines. */
  lineIndex: number;
  /** The cell the type sits in (5 in the standard layout); labels are before it, values after. */
  typeCell: number;
  section: string;
  key: string;
  /** Category, group, entry, trade (empty strings where the row has none). */
  labels: string[];
  fieldType: string;
  /** The value cells as written, blanks and `/` included, for editing. */
  values: string[];
  /** The options a project can store, with their index; empty for rows without a selection type. */
  options: Array<{ index: number; label: string }>;
}

export interface BeschreibungSection {
  name: string;
  /** The text after the name on the section's header line (a title, or column names). */
  title: string;
  lineIndex: number;
  rows: BeschreibungRow[];
}

export interface BeschreibungFile {
  bom: boolean;
  separator: string;
  /** The file split at `\n`; a line keeps its trailing `\r` if it had one. */
  lines: string[];
  sections: BeschreibungSection[];
}

/** The UTF-8 BOM as the binary string `atob` yields. */
const BOM_BINARY = "\u00EF\u00BB\u00BF";

/**
 * A file's bytes (base64) as text, with its BOM noted and removed. UTF-8 is decoded through the
 * `escape` / `decodeURIComponent` pair rather than `TextDecoder`, which the unit-test environment lacks,
 * so the tested code is the code that runs.
 */
export function decodeBeschreibung(contentBase64: string): { text: string; bom: boolean } {
  const binary = atob(contentBase64);
  const bom = binary.startsWith(BOM_BINARY);
  return { text: decodeURIComponent(escape(bom ? binary.slice(3) : binary)), bom };
}

/** The text as base64 bytes (UTF-8), with the BOM put back when the base had one. */
export function encodeBeschreibung(text: string, bom: boolean): string {
  return btoa((bom ? BOM_BINARY : "") + unescape(encodeURIComponent(text)));
}

/** The separator: the last character of the first line, up to its first `\r` (IRES Project's reading). */
export function separatorOf(text: string): string {
  const last = (text.split("\n", 1)[0] ?? "").split("\r")[0].trimEnd().slice(-1);
  return last === "," || last === ";" ? last : ";";
}

/** Splits a line into its raw cells, exactly as written (quotes kept), at separators outside quotes. */
export function splitRawCells(line: string, separator: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;

  for (const char of line) {
    if (char === '"') {
      quoted = !quoted;
    }
    if (char === separator && !quoted) {
      cells.push(cell);
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells;
}

/** A raw cell's CSV value: a quoted cell unquoted, `""` read as one quote; nothing stripped beyond that. */
export function csvValue(rawCell: string): string {
  return rawCell.startsWith('"') && rawCell.endsWith('"') && rawCell.length >= 2
    ? rawCell.slice(1, -1).replace(/""/g, '"')
    : rawCell;
}

/** Splits a line into cells for display, quotes honoured and stripped as IRES Cloud does. */
export function splitCells(line: string, separator: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === separator) {
      cells.push(cell);
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell);
  return cells.map((value) => value.replace(/^"+|"+$/g, "").trim());
}

/** One cell as written back: quoted when it holds the separator or a quote. */
export function joinCell(value: string, separator: string): string {
  return value.includes(separator) || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
}

/** A row's options: the value cells without blanks and `/`, counted from 0. */
function optionsOf(values: string[]): Array<{ index: number; label: string }> {
  return values.filter((value) => value !== "" && value !== "/").map((label, index) => ({ index, label }));
}

/**
 * The number an option is shown by in the portal: its index plus one. A project stores the index
 * (counted from 0), but the Beschreibung's own references count from 1, as IRES Project keys its
 * options: `*Boden0010-3` is the third option of `Boden0010`. Showing the index would make anyone
 * writing a reference point it at the next option.
 */
export function optionNumber(index: number): number {
  return index + 1;
}

/** The values a reference can name, as the row editor holds them: the type and the value cells as written. */
export interface ReferencedValues {
  fieldType: string;
  values: string[];
}

/** A value cell that names something: not blank, and in a row with a type not a `/` row split. */
function isNumbered(fieldType: string, value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== "" && !(TYPE_MARKERS.has(fieldType) && trimmed === "/");
}

/**
 * The number a reference `Key-n` names the value at `position` by, or null for a cell it cannot name.
 * A row with a type (E, M, T, A) is counted by option, blanks and `/` skipped, as IRES Project keys its
 * options and IRES Cloud's check counts them; a row without one (the `Def…` lists, `*Def0010-18`) by
 * cell, blanks included, as IRES Project numbers definitions.
 */
export function referenceNumberAt(row: ReferencedValues, position: number): number | null {
  if (!isNumbered(row.fieldType, row.values[position] ?? "")) {
    return null;
  }
  if (!TYPE_MARKERS.has(row.fieldType)) {
    return position + 1;
  }
  return optionNumber(row.values.slice(0, position).filter((value) => isNumbered(row.fieldType, value)).length);
}

/** The value a reference `Key-number` names in a row (see `referenceNumberAt`), or null when none. */
export function referencedValue(row: ReferencedValues, number: number): string | null {
  const position = row.values.findIndex((_, index) => referenceNumberAt(row, index) === number);
  return position === -1 ? null : row.values[position].trim();
}

/** A reference to one of a row's values, and the key of the row that holds it. */
export interface RowReference {
  number: number;
  heldBy: string;
}

/**
 * Every reference in the file to a value of the row `key`, in file order: `*Boden0010-3`,
 * `(*Boden0010-3)` and a formula's bare `Boden0010-3` alike, but not another key that contains it
 * (`XBoden0010-2`, `Boden00100-1`).
 */
export function referencesTo(file: BeschreibungFile, key: string): RowReference[] {
  const escapedKey = key.replace(/[.*+?^$()|[\]\\{}]/g, "\\$&");
  const pattern = new RegExp(`(?:^|[^A-Za-z0-9])${escapedKey}-(\\d+)`, "g");
  const references: RowReference[] = [];
  file.lines.forEach((line) => {
    const heldBy = (line.split(file.separator)[0] ?? "").replace(/^\uFEFF/, "").replace(/^#/, "").trim();
    // exec on a global pattern walks the line and resets itself at the end, ready for the next line.
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line)) !== null) {
      references.push({ number: Number(match[1]), heldBy });
    }
  });
  return references;
}

/** A referenced number that names another value after an edit, with the rows holding it. */
export interface ShiftedReference {
  number: number;
  before: string;
  /** Null when the number names nothing any more. */
  after: string | null;
  heldBy: string[];
}

/**
 * The references an edit of a row's values would point elsewhere: moving or removing a value renumbers
 * the ones after it, and nothing rewrites the references (the row editor only warns, 2026-10-12). A
 * reference that already named nothing before the edit is left out: the edit did not break it.
 */
export function shiftedReferences(references: RowReference[], before: ReferencedValues, after: ReferencedValues): ShiftedReference[] {
  const shifted = new Map<number, ShiftedReference>();
  for (const { number, heldBy } of references) {
    const was = referencedValue(before, number);
    const now = referencedValue(after, number);
    if (was === null || was === now) {
      continue;
    }
    const entry = shifted.get(number) ?? { number, before: was, after: now, heldBy: [] };
    if (!entry.heldBy.includes(heldBy)) {
      entry.heldBy.push(heldBy);
    }
    shifted.set(number, entry);
  }
  return [...shifted.values()].sort((a, b) => a.number - b.number);
}

/** A data line as a row of the standard columns. */
function rowOf(cells: string[], lineIndex: number, section: string): BeschreibungRow {
  const key = cells[0].replace(/^#/, "");
  // A type marker found elsewhere (older layouts without the trade column) moves the split with it.
  const markerAt = cells.findIndex((cell, i) => i > 0 && TYPE_MARKERS.has(cell));
  const typeAt = markerAt === -1 ? TYPE_CELL : markerAt;
  const labels = cells.slice(1, typeAt);
  while (labels.length < LABEL_COLUMNS.length) labels.push("");
  const fieldType = cells[typeAt] ?? "";
  const values = cells.slice(typeAt + 1);
  while (values.length > 0 && values[values.length - 1] === "") values.pop();

  return {
    lineIndex,
    typeCell: typeAt,
    section,
    key,
    labels,
    fieldType,
    values,
    options: fieldType === "E" || fieldType === "M" ? optionsOf(values) : [],
  };
}

/** Splits a file's text into its lines, sections and rows. */
export function parseBeschreibung(text: string, bom = false): BeschreibungFile {
  const lines = text.split("\n");
  const separator = separatorOf(text);
  const sections: BeschreibungSection[] = [];
  let current: BeschreibungSection | null = null;

  lines.forEach((raw, lineIndex) => {
    const line = raw.replace(/\s+$/, "");
    if (lineIndex === 0 || !line.trim() || line.startsWith("?")) {
      return;
    }
    const cells = splitCells(line, separator);
    if (cells[0].startsWith("#") && !/\d/.test(cells[0])) {
      current = { name: cells[0].slice(1), title: cells.slice(1).filter((cell) => cell !== "").join(" · "), lineIndex, rows: [] };
      sections.push(current);
      return;
    }
    if (current && cells[0].replace(/^#/, "")) {
      current.rows.push(rowOf(cells, lineIndex, current.name));
    }
  });

  return { bom, separator, lines, sections };
}

/** The file's text again: its lines joined at `\n`, each with its own ending. */
export function beschreibungText(file: BeschreibungFile): string {
  return file.lines.join("\n");
}

/** A line's ending (`\r` the files carry before `\n`) and its body. */
function partsOf(line: string): { body: string; ending: string } {
  const ending = /\r+$/.exec(line)?.[0] ?? "";
  return { body: line.slice(0, line.length - ending.length), ending };
}

/** A cell's CSV value, for editing (see the file comment: the hide marker survives). */
export function cellValue(file: BeschreibungFile, lineIndex: number, cell: number): string {
  return csvValue(splitRawCells(partsOf(file.lines[lineIndex] ?? "").body, file.separator)[cell] ?? "");
}

/** The file with one cell of one line set; every other cell and line stays byte for byte. */
export function withCell(file: BeschreibungFile, lineIndex: number, cell: number, value: string): BeschreibungFile {
  const lines = [...file.lines];
  const { body, ending } = partsOf(lines[lineIndex]);
  const cells = splitRawCells(body, file.separator);
  while (cells.length <= cell) cells.push("");
  cells[cell] = joinCell(value, file.separator);
  lines[lineIndex] = cells.join(file.separator) + ending;
  return parseBeschreibung(lines.join("\n"), file.bom);
}

/** The file with a new row (key only) right after the given line, as wide as that line and ending alike. */
export function withRowInserted(file: BeschreibungFile, afterLineIndex: number, key: string): BeschreibungFile {
  const lines = [...file.lines];
  const { body, ending } = partsOf(lines[afterLineIndex]);
  const width = splitRawCells(body, file.separator).length;
  const cells = [joinCell(key, file.separator), ...new Array(Math.max(width - 1, 0)).fill("")];
  lines.splice(afterLineIndex + 1, 0, cells.join(file.separator) + ending);
  return parseBeschreibung(lines.join("\n"), file.bom);
}

/** The file without the given line. */
export function withRowRemoved(file: BeschreibungFile, lineIndex: number): BeschreibungFile {
  const lines = file.lines.filter((_, index) => index !== lineIndex);
  return parseBeschreibung(lines.join("\n"), file.bom);
}

/**
 * The file with its `#Version` line naming a new version and date, the rest of the line kept; IRES
 * Cloud refuses a file whose header names another version than it is published under.
 */
export function withVersionHeader(file: BeschreibungFile, version: string, date: string): BeschreibungFile {
  if (splitCells(partsOf(file.lines[0] ?? "").body, file.separator)[0] !== "#Version") {
    return file;
  }
  return withCell(withCell(file, 0, 1, `V${version}`), 0, 2, date);
}

/** Today as the `#Version` line writes dates: `dd.MM.yy`. */
export function todayLabel(now = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${String(now.getFullYear()).slice(-2)}`;
}

/** A new draft from a published version: its file under the new number, `#Version` line updated. */
export function draftFrom(baseVersion: string, version: string, contentBase64: string, now = new Date()): BeschreibungDraft {
  const { text, bom } = decodeBeschreibung(contentBase64);
  const file = withVersionHeader(parseBeschreibung(text, bom), version, todayLabel(now));
  return { baseVersion, version, bom, text: beschreibungText(file), savedMillis: now.getTime() };
}

/** Whether a key is already used in the file. */
export function keyExists(file: BeschreibungFile, key: string): boolean {
  return file.sections.some((section) => section.rows.some((row) => row.key === key));
}

/** A row's searchable text: key, labels and values. */
export function rowMatches(row: BeschreibungRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === "" || [row.key, ...row.labels, row.fieldType, ...row.values].some((cell) => cell.toLowerCase().includes(needle));
}

/** A draft of a new version, as the browser keeps it until it is published or discarded. */
export interface BeschreibungDraft {
  baseVersion: string;
  version: string;
  bom: boolean;
  text: string;
  savedMillis: number;
}

/** Where the browser keeps the one open draft. */
export const DRAFT_STORAGE_KEY = "ires.admin.beschreibungDraft.v1";

/** The stored draft, or null when there is none or it cannot be read. */
export function readDraft(storage: Pick<Storage, "getItem">): BeschreibungDraft | null {
  try {
    const raw = storage.getItem(DRAFT_STORAGE_KEY);
    const draft = raw ? (JSON.parse(raw) as Partial<BeschreibungDraft>) : null;
    return draft && typeof draft.text === "string" && typeof draft.version === "string" && typeof draft.baseVersion === "string"
      ? { baseVersion: draft.baseVersion, version: draft.version, bom: draft.bom === true, text: draft.text, savedMillis: Number(draft.savedMillis) || 0 }
      : null;
  } catch {
    // A blocked or full storage just means no draft survives a reload.
    return null;
  }
}

/** Keeps the draft; false when the browser refuses (private mode, full storage). */
export function writeDraft(storage: Pick<Storage, "setItem">, draft: BeschreibungDraft): boolean {
  try {
    storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

/** Drops the draft. */
export function clearDraft(storage: Pick<Storage, "removeItem">): void {
  try {
    storage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // Nothing to do: a storage that cannot be written holds no draft either.
  }
}
